// /retake/building — shown after a paying customer finishes a quiz retake
// (started from Account & billing). Kicks report-retake-background with the
// final answer snapshot, polls until the new report is built and linked to
// their account, then drops them on the dashboard. Their previous report
// stays in place until the new one is ready, so a failure never leaves them
// with nothing.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { useQuizStore } from '../store/quizStore';
import { track } from '../lib/analytics';

const POLL_MS = 4000;
const GIVE_UP_MS = 5 * 60 * 1000;

const STEPS = [
  'Reading your new answers',
  'Re-scoring your personality profile',
  'Matching you to 10 career paths',
  'Rebuilding your 90-day plan',
];

type Phase = 'working' | 'ready' | 'failed';

export function RetakeBuilding() {
  const navigate = useNavigate();
  const sessionId = useQuizStore((s) => s.sessionId);
  const retake = useQuizStore((s) => s.retake);
  const [phase, setPhase] = useState<Phase>('working');
  const [step, setStep] = useState(0);
  const attemptStartedAt = useRef<number>(0);
  const pollTimer = useRef<number | undefined>(undefined);

  const kick = useCallback(async () => {
    const s = useQuizStore.getState();
    if (!s.sessionId) return;
    attemptStartedAt.current = Date.now();
    setPhase('working');
    try {
      await fetch('/api/report-retake-background', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_session_id: s.sessionId, variables: s.variables, answers: s.answers }),
      });
    } catch {
      /* polling below decides the outcome */
    }
  }, []);

  const poll = useCallback(async () => {
    const csid = useQuizStore.getState().sessionId;
    if (!csid) return;
    try {
      const res = await fetch(
        `/api/report-retake?status=${encodeURIComponent(csid)}&since=${attemptStartedAt.current - 5000}`,
        { credentials: 'same-origin', cache: 'no-store' },
      );
      const data = (await res.json()) as { ready?: boolean; failed?: boolean };
      if (data.ready) {
        track('retake_report_ready');
        useQuizStore.getState().finishRetake();
        setPhase('ready');
        window.setTimeout(() => navigate('/dashboard'), 1200);
        return;
      }
      if (data.failed || Date.now() - attemptStartedAt.current > GIVE_UP_MS) {
        setPhase('failed');
        return;
      }
    } catch {
      /* transient — keep polling */
    }
    pollTimer.current = window.setTimeout(poll, POLL_MS);
  }, [navigate]);

  const start = useCallback(() => {
    window.clearTimeout(pollTimer.current);
    void kick().then(() => { pollTimer.current = window.setTimeout(poll, POLL_MS); });
  }, [kick, poll]);

  useEffect(() => {
    if (!sessionId || !retake) {
      navigate('/dashboard', { replace: true });
      return;
    }
    start();
    return () => window.clearTimeout(pollTimer.current);
    // Run once per mount — start() is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cosmetic step ticker while the report builds (~30–60s).
  useEffect(() => {
    if (phase !== 'working') return;
    const t = window.setInterval(() => setStep((i) => Math.min(i + 1, STEPS.length - 1)), 9000);
    return () => window.clearInterval(t);
  }, [phase]);

  return (
    <QuizLayout showBack={false} logoHref="/dashboard">
      <p className="eyebrow mb-3 text-center">Retake</p>
      <h1 className="text-display-md text-ink mb-3 text-center">
        {phase === 'ready'
          ? 'Your new report is ready.'
          : phase === 'failed'
            ? "We couldn't finish your new report."
            : 'Rebuilding your report from your new answers.'}
      </h1>
      <p className="text-ink-soft text-base text-center mb-8 max-w-md mx-auto">
        {phase === 'ready'
          ? 'Taking you to your dashboard…'
          : phase === 'failed'
            ? 'Your previous report is still on your dashboard. Try again, or reply to any of our emails and we will sort it out.'
            : 'This usually takes under a minute. Keep this page open.'}
      </p>

      {phase === 'working' && (
        <ul className="space-y-2 mb-8" aria-live="polite">
          {STEPS.map((label, i) => {
            const state = i < step ? 'done' : i === step ? 'current' : 'pending';
            return (
              <li
                key={label}
                className={`flex items-center gap-3 rounded-cta px-4 py-3 ${
                  state === 'done'
                    ? 'bg-canvas-subtle text-ink'
                    : state === 'current'
                      ? 'bg-brand-50 border-2 border-brand text-ink'
                      : 'bg-white border-2 border-ink/10 text-ink-light'
                }`}
              >
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {state === 'done' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  {state === 'current' && <span className="w-3 h-3 rounded-full bg-brand animate-pulse" />}
                  {state === 'pending' && <span className="w-2 h-2 rounded-full bg-ink-light/40" />}
                </span>
                <span className="flex-1 font-semibold text-sm">{label}</span>
              </li>
            );
          })}
        </ul>
      )}

      {phase === 'failed' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" className="btn-primary" onClick={start}>
            Try again
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Back to my dashboard
          </button>
        </div>
      )}
    </QuizLayout>
  );
}
